import type { JSX } from 'react';
import {
  AlertTriangle, Package, GanttChart, LayoutDashboard,
  Brain, ShieldCheck, Network,
} from 'lucide-react';
import type { LucideProps } from 'lucide-react';
import { useContent } from '@data';
import { useT } from '@/i18n';
import type { CncApp } from '@/types';
import { SectionHeader, RevealWrapper, Tag } from '@components/ui';
import './CncApps.css';

type IconComp = React.ComponentType<LucideProps>;

const ICON_MAP = {
  AlertTriangle, Package, GanttChart, LayoutDashboard,
  Brain, ShieldCheck, Network,
} satisfies Record<string, IconComp>;

function isKnownIcon(name: string): name is keyof typeof ICON_MAP {
  return name in ICON_MAP;
}

function FeaturedCard({ app }: { app: CncApp }): JSX.Element {
  const t = useT();
  const Icon = isKnownIcon(app.icon) ? ICON_MAP[app.icon] : null;
  return (
    <article
      className="CncCard CncCard--featured"
      style={{
        '--cnc-color':     app.colorVar,
        '--cnc-color-rgb': app.colorRgbVar,
      } as React.CSSProperties}
      aria-labelledby={`cnc-${app.id}`}
    >
      <div className="CncCard__featured-top">
        <span className="CncCard__icon" aria-hidden="true">
          {Icon !== null && <Icon size={20} strokeWidth={1.5} />}
        </span>
        <span className="CncCard__masterpiece-badge">{t.sections.cnc.masterpiece}</span>
      </div>
      <Tag>{app.tag}</Tag>
      <h3 className="CncCard__name" id={`cnc-${app.id}`}>{app.name}</h3>
      <p className="CncCard__desc">{app.description}</p>
      {app.stats !== undefined && app.stats.length > 0 && (
        <div className="CncCard__stats" aria-label={t.sections.cnc.chip}>
          {app.stats.map(stat => (
            <div key={stat.label} className="CncCard__stat">
              <span className="CncCard__stat-value">{stat.value}</span>
              <span className="CncCard__stat-label">{stat.label}</span>
            </div>
          ))}
        </div>
      )}
      <div className="CncCard__accent" aria-hidden="true" />
    </article>
  );
}

export default function CncApps(): JSX.Element {
  const { CNC_APPS } = useContent();
  const t = useT();

  const featured = CNC_APPS.filter(a => a.isFeatured);
  const regular  = CNC_APPS.filter(a => !a.isFeatured);

  return (
    <section id="cnc" className="section CncApps">
      <SectionHeader
        chip={t.sections.cnc.chip}
        chipVariant="a"
        title={t.sections.cnc.title}
        subtitle={t.sections.cnc.subtitle}
      />

      <div className="CncApps__grid">
        <RevealWrapper delay={1}>
          <article className="CncCard CncList" aria-label={t.sections.cnc.chip}>
            {regular.map(app => {
              const Icon = isKnownIcon(app.icon) ? ICON_MAP[app.icon] : null;
              return (
                <div
                  key={app.id}
                  className="CncList__row"
                  style={{
                    '--cnc-color':     app.colorVar,
                    '--cnc-color-rgb': app.colorRgbVar,
                  } as React.CSSProperties}
                >
                  <span className="CncCard__icon" aria-hidden="true">
                    {Icon !== null && <Icon size={20} strokeWidth={1.5} />}
                  </span>
                  <div className="CncList__body">
                    <div className="CncList__head">
                      <h3 className="CncCard__name" id={`cnc-${app.id}`}>{app.name}</h3>
                      <Tag>{app.tag}</Tag>
                    </div>
                    <p className="CncCard__desc">{app.description}</p>
                  </div>
                </div>
              );
            })}
          </article>
        </RevealWrapper>

        {featured.map((app, i) => (
          <RevealWrapper key={app.id} delay={(i % 3) + 1} className="CncCard-wrapper--featured">
            <FeaturedCard app={app} />
          </RevealWrapper>
        ))}
      </div>
    </section>
  );
}
